// @flow
import _ from 'lodash';
import React, { useEffect } from 'react';
import { Spinner, TextStyle } from '@shopify/polaris';
import queryString from 'query-string';

import { routes } from 'src/routes';
import { app } from 'src/config/';

const regexShop = /^.+[.]myshopify.com$/;
const host = process.env.REACT_APP_ADMIN_V3_BASE_URL; // 'https://staging.bc-solutions.net'
const connectUrl = `${host}/boost-pfs-admin/system/shopify/connect`;
const callbackUrl = `${host}/boost-pfs-admin/admin/klaviyo/oauth/callback`;

// Statuses surfaced back to the Klaviyo integration page so it can show the right banner.
const CALLBACK_STATUS = {
  DENIED: 'denied', // merchant declined the requested permissions on Klaviyo's consent screen
  EXPIRED: 'expired', // authorization code expired/invalid (Klaviyo codes live ~5 min)
  FAILED: 'failed' // anything else we couldn't complete
};

// Always land the merchant back on the Klaviyo integration page with a status, so the
// flow never dead-ends on the "Redirecting..." spinner.
const redirectWithStatus = (status, tenantId) => {
  const shop = tenantId[0];
  const path = `${routes.integration.klaviyo.view()}?status=${status}`;
  window.location = shop
    ? `https://${shop}/admin/apps/${app.apiKey}${path}`
    : `${process.env.REACT_APP_FILTER_ADMIN_HTML_BASE_URL}${path}`;
};

const handleCallback = async location => {
  const queryParams = queryString.parse(location.search);
  const { code, state, error } = queryParams;

  const decodeQuery = decodeURIComponent(state || '');
  const tenantId = decodeQuery
    .split(',')
    .filter(text => regexShop.test(text) && !text.startsWith('shop'));

  try {
    // Klaviyo redirected back with an error (e.g. merchant denied permissions).
    if (!_.isEmpty(error)) {
      redirectWithStatus(
        error === 'access_denied' ? CALLBACK_STATUS.DENIED : CALLBACK_STATUS.FAILED,
        tenantId
      );
      return;
    }

    const rest = decodeQuery
      .split(',')
      .filter(text => !regexShop.test(text) || text.startsWith('shop'));

    const paramsConnect = rest.reduce((cur, next) => {
      const [field, value] = next.split('|');
      return {
        ...cur,
        [field]: value
      };
    }, {});

    const response = await fetch(`${connectUrl}?${queryString.stringify(paramsConnect)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    const dataShop = await response.json();

    if (!dataShop.authToken) {
      redirectWithStatus(CALLBACK_STATUS.FAILED, tenantId);
      return;
    }

    const redirectUri = `${
      process.env.REACT_APP_FILTER_ADMIN_HTML_BASE_URL
    }${routes.integration.klaviyo.callback()}`;

    const responseCallback = await fetch(callbackUrl, {
      method: 'POST',
      body: JSON.stringify({
        state: tenantId[0],
        code,
        redirectURI: redirectUri
      }),
      headers: {
        'Content-Type': 'application/json',
        'x-bc-sf-filter-auth': dataShop.authToken
      }
    });

    const result = await responseCallback.json();

    if (result.statusCode === 200 && !_.isEmpty(result?.data)) {
      const redirectPathSuccess = `https://${tenantId[0]}/admin/apps/${
        app.apiKey
      }${routes.integration.klaviyo.callbackSuccess()}`;

      window.location = redirectPathSuccess;
      return;
    }

    // Token exchange did not succeed — most commonly an expired/invalid authorization code.
    redirectWithStatus(CALLBACK_STATUS.EXPIRED, tenantId);
  } catch (e) {
    console.log('Error', e);
    redirectWithStatus(CALLBACK_STATUS.FAILED, tenantId);
  }
};

const CallbackKlaviyo: React.FC = ({ location }) => {
  useEffect(() => {
    handleCallback(location);
  }, []);

  return (
    <div className="callback-klaviyo">
      <Spinner />
      <p className="callback-klaviyo__title">Redirecting...</p>
      <p className="callback-klaviyo__desc">
        <TextStyle variation="subdued">Please wait while redirect you.</TextStyle>
      </p>
    </div>
  );
};

export default CallbackKlaviyo;
